"use client"

import React from "react"
import { LazyMotion, domAnimation, m, type Variants } from "framer-motion"

export interface BlurTextProps {
  text: string
  delay?: number
  stagger?: number
  className?: string
  animateBy?: "words" | "characters"
  style?: React.CSSProperties
  /** When false the text stays in its hidden state. Pass `isInView` for scroll-triggered reveals. Defaults to true (animates on mount). */
  animateWhen?: boolean
}

const wordVariants: Variants = {
  hidden: { filter: "blur(14px)", opacity: 0, y: 8 },
  visible: {
    filter: "blur(0px)",
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] },
  },
}

const BlurText = ({
  text,
  delay = 0,
  stagger = 0.07,
  className,
  animateBy = "words",
  style,
  animateWhen = true,
}: BlurTextProps) => {
  const elements =
    animateBy === "words"
      ? text.trim().split(/\s+/).filter(Boolean)
      : text.split("")

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger,
        delayChildren: delay,
      },
    },
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.span
        aria-label={text}
        className={className}
        initial="hidden"
        animate={animateWhen ? "visible" : "hidden"}
        variants={containerVariants}
        style={{ display: "inline", ...style }}
      >
        {elements.reduce<React.ReactNode[]>((acc, el, i) => {
          acc.push(
            <m.span
              key={i}
              aria-hidden="true"
              variants={wordVariants}
              style={{ display: "inline-block" }}
            >
              {el}
            </m.span>
          )
          if (animateBy === "words" && i < elements.length - 1) {
            acc.push(" ")
          }
          return acc
        }, [])}
      </m.span>
    </LazyMotion>
  )
}

export default BlurText
